/**
 * Tool Servers Panel
 * 
 * Super admin interface for managing tool servers:
 * - View all registered tool servers
 * - Register new tool servers
 * - Check server health
 * - Grant/revoke organization access
 * - Manage domain access per organization
 */

import React, { useState, useEffect, useCallback } from 'react';
import './ToolServersPanel.css';

interface ToolServer {
  id: number;
  name: string;
  url: string;
  description: string;
  is_core: boolean;
  is_active: boolean;
  health_status: string;
  version: string;
  domains_count: number;
  tools_count: number;
  created_at: string;
  updated_at: string;
  last_health_check: string | null;
}

interface Organization {
  id: number;
  name: string;
  subdomain: string;
}

interface ServerOrganization {
  organization_id: number;
  organization_name: string;
  granted_at: string;
}

interface DomainInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
  toolCount: number;
}

interface DomainAccess extends DomainInfo {
  isEnabled: boolean;
  hasRecord: boolean;
  grantedAt?: string;
}

interface ServerManifest {
  name: string;
  version: string;
  description: string;
  domains: DomainInfo[];
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const ToolServersPanel: React.FC = () => {
  const [servers, setServers] = useState<ToolServer[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Modal states
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [selectedServer, setSelectedServer] = useState<ToolServer | null>(null);
  const [serverOrgs, setServerOrgs] = useState<ServerOrganization[]>([]);
  const [serverManifest, setServerManifest] = useState<ServerManifest | null>(null);
  const [loadingManifest, setLoadingManifest] = useState(false);
  
  // Domain management states
  const [selectedOrgForDomains, setSelectedOrgForDomains] = useState<ServerOrganization | null>(null);
  const [orgDomainAccess, setOrgDomainAccess] = useState<DomainAccess[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [savingDomains, setSavingDomains] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    description: '',
    is_core: false
  });
  const [registering, setRegistering] = useState(false);

  const fetchServers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/tool-servers/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tool servers');
      }

      const data = await response.json();
      if (data.success) {
        setServers(data.servers);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/super-admin/organizations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      if (data.success) {
        setOrganizations(data.data);
      }
    } catch (err: any) {
      console.error('Error fetching organizations:', err);
    }
  }, []);

  useEffect(() => {
    fetchServers();
    fetchOrganizations();
  }, [fetchServers, fetchOrganizations]);

  const handleRegisterServer = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegistering(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/tool-servers/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to register tool server');
      }

      setSuccess(`Tool server "${formData.name}" registered successfully!`);
      setFormData({ name: '', url: '', description: '', is_core: false });
      setShowRegisterForm(false);
      fetchServers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRegistering(false);
    }
  };

  const handleCheckHealth = async (server: ToolServer) => {
    try {
      const response = await fetch(`${API_URL}/tool-servers/${server.id}/health`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Health check completed: ${data.health.status}`);
        fetchServers(); // Refresh to show updated status
      } else {
        setError(`Health check failed: ${data.error}`);
      }
    } catch (err: any) {
      setError(`Health check failed: ${err.message}`);
    }
  };

  const handleToggleActive = async (server: ToolServer) => {
    try {
      const response = await fetch(`${API_URL}/tool-servers/${server.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !server.is_active })
      });

      if (!response.ok) {
        throw new Error('Failed to update server');
      }

      setSuccess(`Server ${server.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchServers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleViewDetails = async (server: ToolServer) => {
    setSelectedServer(server);
    setLoadingManifest(true);
    setServerManifest(null);
    setServerOrgs([]);
    setSelectedOrgForDomains(null);

    try {
      // Fetch server organizations
      const orgsResponse = await fetch(`${API_URL}/tool-servers/${server.id}/organizations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (orgsResponse.ok) {
        const orgsData = await orgsResponse.json();
        if (orgsData.success) {
          setServerOrgs(orgsData.organizations);
        }
      }

      // Fetch manifest
      const manifestResponse = await fetch(`${API_URL}/tool-servers/${server.id}/manifest`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (manifestResponse.ok) {
        const manifestData = await manifestResponse.json();
        if (manifestData.success) {
          setServerManifest(manifestData.manifest);
        }
      }
    } catch (err: any) {
      console.error('Error fetching server details:', err);
    } finally {
      setLoadingManifest(false);
    }
  };

  const handleGrantAccess = async (organizationId: number) => {
    if (!selectedServer) return;

    try {
      const response = await fetch(`${API_URL}/tool-servers/${selectedServer.id}/grant`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ organizationId })
      });

      if (!response.ok) {
        throw new Error('Failed to grant access');
      }

      setSuccess('Access granted successfully');
      handleViewDetails(selectedServer); // Refresh the details
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRevokeAccess = async (organizationId: number) => {
    if (!selectedServer) return;

    try {
      const response = await fetch(`${API_URL}/tool-servers/${selectedServer.id}/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ organizationId })
      });

      if (!response.ok) {
        throw new Error('Failed to revoke access');
      }

      setSuccess('Access revoked successfully');
      handleViewDetails(selectedServer); // Refresh the details
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteServer = async (server: ToolServer) => {
    if (!window.confirm(`Are you sure you want to delete "${server.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/tool-servers/${server.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete server');
      }

      setSuccess('Server deleted successfully');
      fetchServers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Domain access management
  const handleManageDomains = async (org: ServerOrganization) => {
    if (!selectedServer) return;
    
    setSelectedOrgForDomains(org);
    setLoadingDomains(true);
    
    try {
      const response = await fetch(
        `${API_URL}/tool-servers/${selectedServer.id}/domains/${org.organization_id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setOrgDomainAccess(data.domains);
        }
      }
    } catch (err: any) {
      console.error('Error fetching domain access:', err);
      setError('Failed to fetch domain access');
    } finally {
      setLoadingDomains(false);
    }
  };

  const handleToggleDomain = async (domainId: string, isEnabled: boolean) => {
    if (!selectedServer || !selectedOrgForDomains) return;
    
    // Optimistically update UI
    setOrgDomainAccess(prev => 
      prev.map(d => d.id === domainId ? { ...d, isEnabled } : d)
    );
    
    try {
      const response = await fetch(
        `${API_URL}/tool-servers/${selectedServer.id}/domains/${selectedOrgForDomains.organization_id}/toggle`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ domainId, isEnabled })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to toggle domain');
      }
      
      setSuccess(`Domain ${isEnabled ? 'enabled' : 'disabled'} successfully`);
    } catch (err: any) {
      // Revert on error
      setOrgDomainAccess(prev => 
        prev.map(d => d.id === domainId ? { ...d, isEnabled: !isEnabled } : d)
      );
      setError(err.message);
    }
  };

  const handleSaveAllDomains = async () => {
    if (!selectedServer || !selectedOrgForDomains) return;
    
    setSavingDomains(true);
    
    try {
      const response = await fetch(
        `${API_URL}/tool-servers/${selectedServer.id}/domains/${selectedOrgForDomains.organization_id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            domains: orgDomainAccess.map(d => ({
              domainId: d.id,
              isEnabled: d.isEnabled
            }))
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save domain access');
      }
      
      setSuccess('Domain access saved successfully');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingDomains(false);
    }
  };

  const handleRestrictDomain = async (domainId: string) => {
    if (!selectedServer || !selectedOrgForDomains) return;
    
    const domainName = orgDomainAccess.find(d => d.id === domainId)?.name || domainId;
    
    if (!window.confirm(
      `Restrict "${domainName}" to ONLY ${selectedOrgForDomains.organization_name}?\n\n` +
      `This will disable this domain for all other organizations.`
    )) {
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/tool-servers/${selectedServer.id}/domains/restrict`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            domainId,
            allowedOrgId: selectedOrgForDomains.organization_id
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to restrict domain');
      }
      
      const data = await response.json();
      setSuccess(`Domain "${domainName}" restricted to ${selectedOrgForDomains.organization_name} (${data.disabled} other orgs disabled)`);
      
      // Update local state to show enabled
      setOrgDomainAccess(prev => 
        prev.map(d => d.id === domainId ? { ...d, isEnabled: true, hasRecord: true } : d)
      );
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getHealthBadge = (status: string) => {
    const statusLower = status?.toLowerCase() || 'unknown';
    let className = 'health-badge ';
    
    if (statusLower === 'healthy') {
      className += 'healthy';
    } else if (statusLower === 'unhealthy') {
      className += 'unhealthy';
    } else {
      className += 'unknown';
    }

    return <span className={className}>{status || 'Unknown'}</span>;
  };

  const getOrgsWithoutAccess = () => {
    const grantedOrgIds = new Set(serverOrgs.map(o => o.organization_id));
    return organizations.filter(org => !grantedOrgIds.has(org.id));
  };

  return (
    <div className="tool-servers-panel">
      {/* Header */}
      <div className="panel-header">
        <div>
          <h2>🔧 Tool Servers</h2>
          <p>Manage registered tool servers and organization access</p>
        </div>
        <div className="header-actions">
          <button 
            className="primary-button"
            onClick={() => setShowRegisterForm(true)}
          >
            + Register New Server
          </button>
          <button 
            className="secondary-button"
            onClick={fetchServers}
            disabled={loading}
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="alert-close">×</button>
        </div>
      )}

      {/* Server List */}
      <div className="servers-grid">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading tool servers...</p>
          </div>
        ) : servers.length === 0 ? (
          <div className="empty-state">
            <h3>No Tool Servers Registered</h3>
            <p>Register your first tool server to enable custom tools for organizations.</p>
            <button 
              className="primary-button"
              onClick={() => setShowRegisterForm(true)}
            >
              Register Tool Server
            </button>
          </div>
        ) : (
          servers.map(server => (
            <div key={server.id} className={`server-card ${!server.is_active ? 'inactive' : ''}`}>
              <div className="server-header">
                <div className="server-title">
                  <span className="server-icon">{server.is_core ? '⚡' : '🔌'}</span>
                  <div>
                    <h3>{server.name}</h3>
                    <span className="server-url">{server.url}</span>
                  </div>
                </div>
                <div className="server-badges">
                  {server.is_core && <span className="badge core">Core</span>}
                  {!server.is_active && <span className="badge inactive">Inactive</span>}
                  {getHealthBadge(server.health_status)}
                </div>
              </div>

              <p className="server-description">{server.description}</p>

              <div className="server-stats">
                <div className="stat">
                  <span className="stat-value">{server.domains_count}</span>
                  <span className="stat-label">Domains</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{server.tools_count}</span>
                  <span className="stat-label">Tools</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{server.version || 'N/A'}</span>
                  <span className="stat-label">Version</span>
                </div>
              </div>

              <div className="server-actions">
                <button 
                  className="action-btn view"
                  onClick={() => handleViewDetails(server)}
                  title="View Details"
                >
                  👁️ Details
                </button>
                <button 
                  className="action-btn health"
                  onClick={() => handleCheckHealth(server)}
                  title="Check Health"
                >
                  🩺 Health
                </button>
                <button 
                  className={`action-btn ${server.is_active ? 'deactivate' : 'activate'}`}
                  onClick={() => handleToggleActive(server)}
                  title={server.is_active ? 'Deactivate' : 'Activate'}
                >
                  {server.is_active ? '⏸️ Deactivate' : '▶️ Activate'}
                </button>
                {!server.is_core && (
                  <button 
                    className="action-btn delete"
                    onClick={() => handleDeleteServer(server)}
                    title="Delete Server"
                  >
                    🗑️ Delete
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Register Server Modal */}
      {showRegisterForm && (
        <div className="modal-overlay" onClick={() => setShowRegisterForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Register New Tool Server</h2>
            </div>
            <form onSubmit={handleRegisterServer}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="server-name">Server Name *</label>
                  <input
                    type="text"
                    id="server-name"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Inventory Management Tools"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="server-url">Server URL *</label>
                  <input
                    type="url"
                    id="server-url"
                    value={formData.url}
                    onChange={e => setFormData({ ...formData, url: e.target.value })}
                    placeholder="e.g., https://tools.example.com"
                    required
                  />
                  <small>Must expose /manifest, /domains/:id, /execute, and /health endpoints</small>
                </div>

                <div className="form-group">
                  <label htmlFor="server-description">Description</label>
                  <textarea
                    id="server-description"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the tools provided by this server..."
                    rows={3}
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_core}
                      onChange={e => setFormData({ ...formData, is_core: e.target.checked })}
                    />
                    <span>Core Server (automatically granted to all organizations)</span>
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="secondary-button"
                  onClick={() => setShowRegisterForm(false)}
                  disabled={registering}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={registering}
                >
                  {registering ? 'Registering...' : 'Register Server'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Server Details Modal */}
      {selectedServer && (
        <div className="modal-overlay" onClick={() => setSelectedServer(null)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{selectedServer.name}</h2>
                <span className="modal-subtitle">{selectedServer.url}</span>
              </div>
              <button 
                className="modal-close"
                onClick={() => setSelectedServer(null)}
              >
                ×
              </button>
            </div>

            <div className="modal-body details-body">
              {loadingManifest ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading server details...</p>
                </div>
              ) : (
                <>
                  {/* Domains Section */}
                  <div className="details-section">
                    <h3>📦 Available Domains</h3>
                    {serverManifest?.domains && serverManifest.domains.length > 0 ? (
                      <div className="domains-list">
                        {serverManifest.domains.map(domain => (
                          <div key={domain.id} className="domain-item">
                            <span className="domain-icon">{domain.icon}</span>
                            <div className="domain-info">
                              <strong>{domain.name}</strong>
                              <span>{domain.description}</span>
                            </div>
                            <span className="domain-tools">{domain.toolCount} tools</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-data">No domains available or unable to fetch manifest</p>
                    )}
                  </div>

                  {/* Organization Access Section */}
                  <div className="details-section">
                    <h3>🏢 Organization Access</h3>
                    
                    {/* Organizations with access */}
                    <div className="access-list">
                      <h4>Organizations with Access ({serverOrgs.length})</h4>
                      {serverOrgs.length > 0 ? (
                        <ul>
                          {serverOrgs.map(org => (
                            <li key={org.organization_id} className="org-access-item">
                              <div className="org-info">
                                <span className="org-name">{org.organization_name}</span>
                                <span className="granted-date">
                                  Granted: {new Date(org.granted_at).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="org-actions">
                                <button 
                                  className="domains-btn"
                                  onClick={() => handleManageDomains(org)}
                                  title="Manage Domain Access"
                                >
                                  📦 Domains
                                </button>
                                <button 
                                  className="revoke-btn"
                                  onClick={() => handleRevokeAccess(org.organization_id)}
                                  title="Revoke Access"
                                >
                                  Revoke
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="no-data">No organizations have access</p>
                      )}
                    </div>

                    {/* Grant access dropdown */}
                    {getOrgsWithoutAccess().length > 0 && (
                      <div className="grant-access">
                        <h4>Grant Access to Organization</h4>
                        <div className="grant-form">
                          <select 
                            id="grant-org-select"
                            defaultValue=""
                            onChange={e => {
                              if (e.target.value) {
                                handleGrantAccess(parseInt(e.target.value));
                                e.target.value = '';
                              }
                            }}
                          >
                            <option value="">Select organization...</option>
                            {getOrgsWithoutAccess().map(org => (
                              <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                          </select>
                        </div>
                        {selectedServer.is_core && (
                          <p className="core-note">
                            ⚡ Core server - grant access to enable tools for each organization
                          </p>
                        )}
                      </div>
                    )}

                    {getOrgsWithoutAccess().length === 0 && serverOrgs.length > 0 && (
                      <p className="core-note">
                        ✅ All organizations have access to this server
                      </p>
                    )}
                  </div>

                  {/* Domain Access Management for Selected Org */}
                  {selectedOrgForDomains && (
                    <div className="details-section domain-access-section">
                      <div className="section-header">
                        <h3>
                          🔐 Domain Access for {selectedOrgForDomains.organization_name}
                        </h3>
                        <button 
                          className="close-section-btn"
                          onClick={() => setSelectedOrgForDomains(null)}
                        >
                          ×
                        </button>
                      </div>
                      
                      {loadingDomains ? (
                        <div className="loading-state small">
                          <div className="loading-spinner"></div>
                          <p>Loading domain access...</p>
                        </div>
                      ) : (
                        <>
                          <p className="section-description">
                            Toggle domains on/off for this organization. Disabled domains won't be available in their chat.
                          </p>
                          <div className="domain-toggles">
                            {orgDomainAccess.map(domain => (
                              <div key={domain.id} className={`domain-toggle-item ${domain.isEnabled ? 'enabled' : 'disabled'}`}>
                                <div className="domain-toggle-info">
                                  <span className="domain-icon">{domain.icon}</span>
                                  <div>
                                    <strong>{domain.name}</strong>
                                    <span className="domain-id">{domain.id}</span>
                                  </div>
                                </div>
                                <div className="domain-toggle-actions">
                                  <button
                                    className="restrict-btn"
                                    onClick={() => handleRestrictDomain(domain.id)}
                                    title="Make this domain exclusive to this org only"
                                  >
                                    🔒 Only this org
                                  </button>
                                  <label className="toggle-switch">
                                    <input
                                      type="checkbox"
                                      checked={domain.isEnabled}
                                      onChange={e => handleToggleDomain(domain.id, e.target.checked)}
                                    />
                                    <span className="toggle-slider"></span>
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="domain-access-actions">
                            <span className="enabled-count">
                              {orgDomainAccess.filter(d => d.isEnabled).length} of {orgDomainAccess.length} domains enabled
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Server Info */}
                  <div className="details-section">
                    <h3>ℹ️ Server Information</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Version</span>
                        <span className="info-value">{selectedServer.version || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Health Status</span>
                        <span className="info-value">{getHealthBadge(selectedServer.health_status)}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Last Health Check</span>
                        <span className="info-value">
                          {selectedServer.last_health_check 
                            ? new Date(selectedServer.last_health_check).toLocaleString()
                            : 'Never'}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Registered</span>
                        <span className="info-value">
                          {new Date(selectedServer.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolServersPanel;
